/* eslint-disable @typescript-eslint/indent */

/* Note: please be careful with indentation, we have the rule disabled because of
* eslint not being to properly indent js inside styled components
*/

// Core
import styled, {
  css,
  keyframes,
  Keyframes,
} from 'styled-components';

// Plugins
import { hexToRgba } from '../../../../../../plugins/general';

// Config
import { BREAKPOINTS } from '../../../../../../config/screen';

// Components
import { Button } from '../../../../../../layout/elements/Button';

/* *************************************************************************************************
******************************************* CONSTANTS **********************************************
************************************************************************************************* */

const PULSE_ANIMATION_TIMING: string = '4s infinite ease-in-out';
const TRANSITION_TIMING: string = '0.3s ease';

/* *************************************************************************************************
******************************************* ANIMATIONS *********************************************
************************************************************************************************* */

/**
 * Glow animation (used in the ::after of the button and in the main container)
 *
 * @param activeAlarmBackgroundColor - The color of the active alarm background
 * @returns - The keyframes for the glow animation
 */
const pulseGlow = (activeAlarmBackgroundColor: string): Keyframes => keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 transparent;
  };
  50% {
    box-shadow: 0 0 3.5px 1px ${activeAlarmBackgroundColor};
  };
`;

/**
 * Background animation of the button
 *
 * @param activeAlarmBackgroundColor - The color of the active alarm background
 * @returns - The keyframes for the background animation
 */
const pulseRedBackground = (activeAlarmBackgroundColor: string): Keyframes => keyframes`
  0%, 100% {
    background-color: inherit;
  };
  50% {
    background-color: ${hexToRgba(activeAlarmBackgroundColor, 0.25)};
  };
`;

/**
 * Icon scale animation of the button
 *
 * @param activeAlarmBackgroundColor - The color of the active alarm background
 * @returns - The keyframes for the icon scale animation
 */
const pulseIconScale = (activeAlarmBackgroundColor: string): Keyframes => keyframes`
  0%, 100% {
    transform: scale(1);
    color: inherit;
  };
  50% {
    transform: scale(1.35);
    color: ${activeAlarmBackgroundColor};
  };
`;

export const AlarmButton = styled(Button)<{ hasActiveAlarms: boolean }>`
  position: relative;
  isolation: isolate; 
  transition: background-color ${TRANSITION_TIMING};

  /* Layer ::after (to cover the grey border !important) */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 3px;
    box-shadow: 0 0 0 0 transparent;
    z-index: 1;
    pointer-events: none;
    transition: box-shadow ${TRANSITION_TIMING};
  };

  /* Icon styles */
  > svg {
    position: relative;
    z-index: 2;
    transition: color ${TRANSITION_TIMING}, transform ${TRANSITION_TIMING};
  };

  /* Alarm indicator */
  .alarms-indicator {
    position: absolute;
    left: 25px;
    bottom: 28px;
    width: 24px;
    height: 18px;
    color: ${({
      theme,
    }) => theme.color.crusherDashboard.detailsModule.activeAlarmTextColor};
    font-size: 12px;
    line-height: normal;
    font-family: ${({ theme }) => theme.font.medium};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    background-color: ${({
     theme,
    }) => theme.color.crusherDashboard.detailsModule.activeAlarmBackgroundColor};
    z-index: 3;
  };

  /* Animation logic */
  ${({ hasActiveAlarms, theme }) => {
    // Define the alarm color
    const { activeAlarmBackgroundColor } = theme.color.crusherDashboard.detailsModule;

    if (hasActiveAlarms) {
      return css`
        /* --- Apply the animations (reusing the constants) --- */
        &:not(.ant-btn-link) {
          animation: ${pulseRedBackground(activeAlarmBackgroundColor)} ${PULSE_ANIMATION_TIMING};
        };
        
        &::after {
          animation: ${pulseGlow(activeAlarmBackgroundColor)} ${PULSE_ANIMATION_TIMING};
        };

        > svg {
          animation: ${pulseIconScale(activeAlarmBackgroundColor)} ${PULSE_ANIMATION_TIMING};
        };

        /** Override the icon SVG color to inherit during hover, even with the animation running. */
        &:hover:not(:disabled) {
          > svg {
            color: inherit !important;
          };
        };
      `;
    }
    return css`
      /* Reset the animations if 'hasActiveAlarms' is false */
      &:not(.ant-btn-link) {
        background-color: inherit !important;
        animation: none;
      };
      /* Reset the glow (for the ::after) */
      &::after {
        animation: none;
      };
      /* Reset the icon */
      > svg {
        color: inherit;
        transform: scale(1);
        animation: none;
      };
    `;
  }}
`;

export const DetailsContainer = styled.div<{ hasActiveAlarms: boolean }>`
  background-color: ${({ theme }) => theme.color.crusherDashboard.modulesBackgroundColor};
  padding: 0.75rem 1rem 1rem;
  border-radius: 4px;
  flex: 1;
  display: flex;
  height: 100%;
  transition: box-shadow ${TRANSITION_TIMING};

  // Increase padding for larger screens
  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.5rem 1.25rem;
  };

  /* ******************* ACTIVE ALARMS ******************* */
  ${({ hasActiveAlarms, theme }) => hasActiveAlarms && css`
    /* Reuse the animation constant 'pulseGlow' */
    animation: ${
      pulseGlow(theme.color.crusherDashboard.detailsModule.activeAlarmBackgroundColor)
    } ${PULSE_ANIMATION_TIMING};
  `}
`;

/* *************************************************************************************************
******************************************** VIEW STYLES *******************************************
************************************************************************************************* */

export const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;

  // Increase gap for larger screens
  @media (min-width: ${BREAKPOINTS.sm}px) {
    gap: 18px;
  };
`;

export const CrusherStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const EquipmentStatusContainer = styled.div`
  margin-bottom: 0.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const EquipmentName = styled.div`
  > sup {
    margin-top: 0.25rem;
    margin-right: 0.25rem;
    font-family: ${({ theme }) => theme.font.bold};
    font-size: 16px;
  };
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  align-items: flex-end;
  width: 100%;
`;

export const OverviewWrapper = styled.div`
  display: flex;
  flex: 1;

  /* Tablet layout: side by side */
  @media (min-width: ${BREAKPOINTS.md}px) {
    gap: 12px;
  };
`;

export const OverviewContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;

  // Remove gap for larger screens
  @media (min-width: ${BREAKPOINTS.sm}px) {
    gap: 0;
  };
`;

export const EquipmentImageContainer = styled.div`
  display: none;

  /* Show on tablet */
  @media (min-width: ${BREAKPOINTS.md}px) {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: max-content;
    max-height: 220px;
    padding-bottom: 2rem;

    img {
      width: 80%;
      height: auto;
      max-height: 80%;
      object-fit: contain;
      margin-bottom: 3rem;
    };
  };

  /* Show on tablet and larger */
  @media (min-width: ${BREAKPOINTS.lg}px) {
    img {
      width: 65%;
      height: auto;
      max-height: 80%;
      object-fit: contain;
      margin-bottom: 4rem;
    };
  };
`;

export const EquipmentImageContainerBelow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: max-content;
  max-height: 220px;
  padding-bottom: 2rem;

  img {
    width: 80%;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
  };
`;

export const CrusherInfoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

export const CloudConnectionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex: 1;
`;

export const CloudConnectionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const TooltipIconContainer = styled.div`
  color: ${({ theme }) => theme.color.crusherDashboard.detailsModule.iconInfoColor};
  margin-top: 4px; // Using pixels for consistency
`;

export const LastUpdateTime = styled.span`
  font-family: ${({ theme }) => theme.font.bold};
  color: ${({ theme }) => theme.color.crusherDashboard.detailsModule.lastUpdateTimeTextColor};
`;

export const LastCloudConnection = styled.span`
  font-family: ${({ theme }) => theme.font.bold};
`;

export const RegisteredMark = styled.sup`
  font-family: ${({ theme }) => theme.font.bold};
  font-size: 18px;
`;

export const OverviewItemsContainer = styled.div`
  max-height: 160px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const OverviewItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
`;
